// src/components/DashboardLayout.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Avatar,
  Badge,
  IconButton,
  Paper,
  Divider,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
  LinearProgress 
} from '@mui/material';
import {
  Notifications,
  Home,
  Task as TaskIcon,
  ListAlt as MyTasksIcon,
  Payment as PaymentsIcon,
  AccountCircle as ProfileIcon,
  Forum as CommunityIcon,
  Help as HelpIcon,
  Logout,
  Menu as MenuIcon,
  AccountBalanceWallet,
  Chat,
  Close
} from '@mui/icons-material';
import ChatDialog from './ChatSystem/ChatDialog';

export default function DashboardLayout({ user, userData, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notificationPolling, setNotificationPolling] = useState(null);  
  const [hasNotifications, setHasNotifications] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationInterval, setNotificationInterval] = useState(null);
  
// Notification messages pool for variety and creativity
const notificationMessages = [
  "just scored £{amount} for a luxury property shoot! 🏰",
  "earned £{amount} completing a commercial verification! 💼",
  "just banked £{amount} from a premium home tour! ✨",
  "pocketed £{amount} for a high-end neighborhood survey! 📍",
  "just made £{amount} with an exclusive photography gig! 📸",
  "earned £{amount} verifying luxury rental prices! 🌆",
  "just got £{amount} for documenting a new development! 🏗️",
  "scored £{amount} completing multiple premium tasks! 🎯",
  "just earned £{amount} from a commercial property audit! 🏢",
  "banked £{amount} for a quick verification task! ⚡"
];

// Random names for notifications
const randomNames = [
"Oliver", "Amelia", "Harry", "Isla", "George", "Emily", "Jack", "Sophia", "Noah", 
"Lily", "Charlie", "Ava", "Thomas", "Mia", "James", "Grace", "William", "Ella", 
"Benjamin", "Daisy", "Henry", "Freya", "Samuel", "Scarlett", "Jacob", "Poppy", "Alexander", "Evie", "Daniel", "Chloe", "Lucas", "Sophie", "Matthew", "Ruby", "Oscar", "Florence", "Ethan", "Alice", "Leo", "Isabelle", "Riley", "Holly", "Arthur", "Ivy", "Nathan", "Lucy", "Max", "Eliza", "Theodore", "Elsie", "Joshua", "Willow", "Adam", "Phoebe", "Logan", "Molly", "Joseph", "Zara", "Emma", "Lewis", "Millie", "Harrison", "Freddie", "Olivia", "Ryan", "Maisie", "Reggie", "Harper", "Violet", "Elliot", "Stanley", "Sienna", "Alfred", "Bonnie", "Aiden", "Tilly", "Louis", "Rosie", "Clara", "Elijah", "Beatrice", "Lydia", "Hugo", "Edward", "Evelyn", "Ralph", "Esme", "Annabelle", "Nicholas", "Orla", "Toby", "Nora", "Caleb", "Luna", "Matilda", "Isaac", "Zara", "Alex", "Jordan", "Taylor", "Morgan", "Casey", "Riley", "Avery", "Peyton", "Quinn", "Blake", "Sam", "Jamie", "Dakota", "Skyler", "Rowan", "London", "Brooklyn", "Paris", "Sydney", "Tokyo", "Rio", "Milan", "Vienna"
];

// Function to generate random notification
const generateRandomNotification = () => {
  const randomName = randomNames[Math.floor(Math.random() * randomNames.length)];
  const randomAmount = (Math.floor(Math.random() * (5000 - 1200 + 1)) + 1200).toLocaleString();
  const randomMessage = notificationMessages[Math.floor(Math.random() * notificationMessages.length)];
  
  return `${randomName} ${randomMessage.replace('{amount}', randomAmount)}`;
};

// Function to show a notification
const showRandomNotification = () => {
  const message = generateRandomNotification();
  setNotificationMessage(message);
  setShowNotification(true);
  setHasNotifications(true); // Keep dot visible after first notification
  
  // Auto-hide notification after 6 seconds
  setTimeout(() => {
    setShowNotification(false);
  }, 4000);
};

// Function to start the notification interval
const startNotificationInterval = () => {
  // Clear any existing interval first
  if (notificationInterval) {
    clearInterval(notificationInterval);
  }
  
  // Set up random interval between 30 seconds and 2 minutes
  const interval = setInterval(() => {
    showRandomNotification();
  }, Math.floor(Math.random() * (30000 - 5000 + 1)) + 5000);
  
  setNotificationInterval(interval);
};


// Initialize notification system when component mounts
useEffect(() => {
  startNotificationInterval();
  
  // Cleanup on component unmount
  return () => {
    if (notificationInterval) {
      clearInterval(notificationInterval);
    }
  };
}, []);

// Fetch user balance
  const fetchUserBalance = async () => {
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/user/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setBalance(userData.balance);
      } else {
        console.error('Failed to fetch user balance');
        setBalance(0); // Default to 0 if fetch fails
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
      setBalance(0); // Default to 0 on error
    } finally {
      setBalanceLoading(false);
    }
  };

  // Refresh balance when user changes or on component mount
  useEffect(() => {
    if (user?.id) {
      fetchUserBalance();
    } else {
      // If no user prop provided, fetch it ourselves
      fetchUserBalance();
    }
  }, [user?.id]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
    handleMenuClose();
  };

  const handleProfileNavigate = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleEarningsNavigate = () => {
    navigate('/earnings');
    handleMenuClose();
  };

  const drawerWidth = 240;
  
// REPLACE the current handleChatClick function with this:
const handleChatClick = async () => {
  try {
    const token = localStorage.getItem('token');
    
    // Check if user has any active conversations
    const response = await fetch('https://propertyearn.net/api/api/chat/conversations', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('📋 Chat check - Active conversations:', data.active_conversations); // Debug log
      
      // 🚨 FIX: Check if there are ANY conversations (active or not)
      if (data.total_conversations > 0) {
        // User has conversations - open chat dialog
        console.log('✅ Opening chat dialog with existing conversations');
        setChatOpen(true);
      } else {
        // No conversations - redirect to help center to start one
        console.log('❌ No conversations found, redirecting to help center');
        navigate('/help#contact-form');
      }
    } else {
      // If API fails, redirect to help center
      console.log('❌ API failed, redirecting to help center');
      navigate('/help#contact-form');
    }
  } catch (error) {
    console.error('❌ Error checking chat status:', error);
    // On error, redirect to help center
    navigate('/help#contact-form');
  }
};
/////////////////////////////////////////////////////////////////////
// ADD this function to check for unread messages
const checkUnreadMessages = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('https://propertyearn.net/api/api/chat/conversations', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      
      // Calculate total unread messages across all conversations
      let totalUnread = 0;
      if (data.conversations && data.conversations.length > 0) {
        data.conversations.forEach(conversation => {
          totalUnread += conversation.unread_count || 0;
        });
      }
      
      console.log('📬 Unread messages count:', totalUnread);
      setUnreadMessages(totalUnread);
      
      return totalUnread;
    }
  } catch (error) {
    console.error('❌ Error checking unread messages:', error);
  }
  return 0;
};

// ADD this useEffect for real-time notification updates
useEffect(() => {
  if (user?.id) {
    // Check immediately when component loads
    checkUnreadMessages();
    
    // Set up polling every 10 seconds for new messages
    const interval = setInterval(() => {
      checkUnreadMessages();
    }, 10000); // Check every 10 seconds
    
    // Store interval reference for cleanup
    setNotificationPolling(interval);
    
    // Cleanup interval on component unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }
}, [user?.id]); // Re-run when user changes


const pulsingAnimation = `
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 5px rgba(244, 67, 54, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}
`;

  const drawerContent = (
    <Box>
      <List>
        {[
          { text: 'Dashboard Overview', icon: <Home />, path: '/dashboard' },
          { text: 'Task Marketplace', icon: <TaskIcon />, path: '/tasks' },
          { text: 'My Tasks', icon: <MyTasksIcon />, path: '/my-tasks' },
          { text: 'Earnings & Payments', icon: <PaymentsIcon />, path: '/earnings' },
          { text: 'Profile & Settings', icon: <ProfileIcon />, path: '/profile' },
          { text: 'Community', icon: <CommunityIcon />, path: '/community' },
          { text: 'Help Center', icon: <HelpIcon />, path: '/help' }
        ].map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            selected={location.pathname === item.path}
            sx={{
              mb: 0.5,
              mx: 1,
              borderRadius: 1,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'green',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              },
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: location.pathname === item.path ? 'green' : 'inherit',
              minWidth: 40 
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text} 
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: '0.9rem',
                  color: location.pathname === item.path ? 'green' : 'inherit'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Mobile Bottom Navigation
  const MobileBottomNav = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const mainNavigationItems = [
      { text: 'Dashboard', icon: <Home />, path: '/dashboard' },
      { text: 'Tasks', icon: <TaskIcon />, path: '/tasks' },
      { text: 'Earnings', icon: <PaymentsIcon />, path: '/earnings' },
      { text: 'Profile', icon: <ProfileIcon />, path: '/profile' }
    ];

    const allNavigationItems = [
      { text: 'Dashboard', icon: <Home />, path: '/dashboard' },
      { text: 'Task Marketplace', icon: <TaskIcon />, path: '/tasks' },
      { text: 'My Tasks', icon: <MyTasksIcon />, path: '/my-tasks' },
      { text: 'Earnings & Payments', icon: <PaymentsIcon />, path: '/earnings' },
      { text: 'Profile & Settings', icon: <ProfileIcon />, path: '/profile' },
      { text: 'Community', icon: <CommunityIcon />, path: '/community' },
      { text: 'Help Center', icon: <HelpIcon />, path: '/help' }
    ];

    return (
      <>
        {/* Main Bottom Navigation Bar */}
        <Paper sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'space-around',
          alignItems: 'center',
          p: 1,
          zIndex: 1000,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          {mainNavigationItems.map((item) => (
            <IconButton
              key={item.text}
              color={location.pathname === item.path ? 'primary' : 'default'}
              onClick={() => navigate(item.path)}
              aria-label={item.text}
              sx={{
                flexDirection: 'column',
                fontSize: '0.75rem',
                gap: 0.5
              }}
            >
              {item.icon}
              <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1 }}>
                {item.text}
              </Typography>
            </IconButton>
          ))}
          
          {/* Menu Button for additional navigation items */}
          <IconButton
            color="inherit"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="More menu"
            sx={{
              flexDirection: 'column',
              fontSize: '0.75rem',
              gap: 0.5
            }}
          >
            <MenuIcon />
            <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1 }}>
              More
            </Typography>
          </IconButton>
        </Paper>

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="bottom"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              height: 'auto',
              maxHeight: '70vh',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
              Navigation Menu
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {allNavigationItems.map((item) => (
                <ListItem
                  button
                  key={item.text}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: location.pathname === item.path ? 'white' : 'inherit',
                    minWidth: 40 
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: location.pathname === item.path ? 'white' : 'inherit',
                        fontSize: '0.9rem'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Desktop Navigation Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            position: 'relative',
            border: 'none',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
          },
        }}
        open
      >
        <Box sx={{ 
          p: 2, 
          textAlign: 'center', 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 50 // Adjust based on your logo size
        }}>
          {/* Logo Image */}
    {/* Scale up/down using CSS transform */}
  <img 
    src="/images/logo.png" 
    alt="PropertyEarn Logo" 
    style={{ 
      width: '200px',
      height: '40px',
      objectFit: 'contain',
      transform: 'scale(0.9)', // Adjust this value: 0.7 = 70% size
      display: 'block'
    }}
  />
</Box>

        {drawerContent}
      </Drawer>

      <Box sx={{ flexGrow: 1, pb: { xs: 8, md: 0 } }}>
        {/* Top Header Bar */}
        <Paper sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          p: 2,
          borderRadius: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #137c55ff 0%, #21874dff 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ position: 'relative', mr: 2, display: 'flex', alignItems: 'center' }}>
              <IconButton 
                onClick={handleProfileMenuOpen} 
                sx={{ 
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }} 
                aria-label="user menu"
              >
                <Avatar sx={{ width: 48, height: 48, backgroundColor: 'rgba(255,255,255,0.2)' }}>
                  <ProfileIcon fontSize="large" />
                </Avatar>
              </IconButton>
              <Chip
                label={user?.tier || 'Basic'}
                size="small"
                color={user?.tier === 'Pro' ? 'secondary' : user?.tier === 'Verified' ? 'primary' : 'default'}
                sx={{ 
                  position: 'absolute', 
                  bottom: -8, 
                  right: -8, 
                  fontSize: '0.6rem',
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontWeight: 'bold'
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {user?.name || user?.email || 'User'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWallet sx={{ fontSize: 16 }} />
                {balanceLoading ? (
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                ) : (
                  <Typography variant="body2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    £{typeof balance === 'number' ? balance.toFixed(2) : '0.00'}
                  </Typography>
                )}
              </Box>
            
            </Box>
          </Box>

            {/* Chat */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Chat Icon with Notification Badge */}
           <IconButton 
            color="inherit"
            onClick={handleChatClick}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              },
              position: 'relative'
            }}
          >
            {/* Notification Badge - shows red dot with unread count */}
            <Badge 
              badgeContent={unreadMessages} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  height: '18px',
                  minWidth: '18px',
                  backgroundColor: '#f44336', // Red color for notifications
                  animation: unreadMessages > 0 ? 'pulse 1.5s infinite' : 'none', // Pulsing animation for new messages
                }
              }}
            >
              <Chat />
            </Badge>
            
                    {/* Optional: Add tooltip */}
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: unreadMessages > 0 ? '#f44336' : 'transparent',
                        display: unreadMessages > 0 ? 'block' : 'none',
                      }}
                    />
              </IconButton>

            {/* Notifications Icon with Dot Indicator */}
            <IconButton 
              color="inherit"
              onClick={() => {
                setShowNotification(true);
                setHasNotifications(false); // Remove dot when clicked
                // Show a new random notification when manually clicked
                const message = generateRandomNotification();
                setNotificationMessage(message);
              }}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                },
                position: 'relative'
              }}
            >
              {/* Custom Badge for Notification Dot */}
              <Box sx={{ position: 'relative' }}>
                <Notifications />
                {/* Red dot indicator - shows when there are notifications */}
                {hasNotifications && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 12,
                      height: 12,
                      backgroundColor: '#ff4444',
                      borderRadius: '50%',
                      border: '2px solid #137c55', // Match header background
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { 
                          transform: 'scale(0.95)', 
                          boxShadow: '0 0 0 0 rgba(255, 68, 68, 0.7)' 
                        },
                        '70%': { 
                          transform: 'scale(1.1)', 
                          boxShadow: '0 0 0 6px rgba(255, 68, 68, 0)' 
                        },
                        '100%': { 
                          transform: 'scale(0.95)', 
                          boxShadow: '0 0 0 0 rgba(255, 68, 68, 0)' 
                        }
                      }
                    }}
                  />
                )}
              </Box>
            </IconButton>
            </Box>

          {/* Profile Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1
                }
              }
            }}
          >
            <MenuItem onClick={handleProfileNavigate}>
              <ListItemIcon>
                <ProfileIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">Profile & Settings</Typography>
            </MenuItem>
            <MenuItem onClick={handleEarningsNavigate}>
              <ListItemIcon>
                <PaymentsIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">Earnings & Balance</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">Logout</Typography>
            </MenuItem>
          </Menu>
        </Paper>

        {/* Main Content Area */}
        <Box component="main" sx={{ 
          p: { xs: 2, sm: 3 },
          minHeight: 'calc(100vh - 80px)',
          pb: { xs: 10, md: 3 }
        }}>
          {children}
        </Box>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav />
      </Box>

        {/* ADD CHAT DIALOG */}
          <ChatDialog 
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          user={user}
          onMarkAsRead={checkUnreadMessages} // Reset unread count when chat open
          />


            {/* Floating Notification Display */}
            {showNotification && (
              <Paper
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 20,
                  zIndex: 9999,
                  minWidth: 320,
                  maxWidth: 400,
                  p: 2,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  border: '2px solid',
                  borderColor: 'primary.dark',
                  animation: 'slideIn 0.5s ease-out, glow 2s infinite',
                  '@keyframes slideIn': {
                    '0%': { transform: 'translateX(100%)', opacity: 0 },
                    '100%': { transform: 'translateX(0)', opacity: 1 }
                  },
                  '@keyframes glow': {
                    '0%, 100%': { 
                      boxShadow: '0 8px 32px rgba(19, 124, 85, 0.3)',
                      borderColor: 'primary.dark'
                    },
                    '50%': { 
                      boxShadow: '0 8px 32px rgba(19, 124, 85, 0.6), 0 0 20px rgba(19, 124, 85, 0.4)',
                      borderColor: 'primary.light'
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  {/* Notification Icon */}
                  <Box sx={{ 
                    animation: 'bounce 2s infinite',
                    '@keyframes bounce': {
                      '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                      '40%': { transform: 'translateY(-5px)' },
                      '60%': { transform: 'translateY(-3px)' }
                    }
                  }}>
                    <Notifications sx={{ color: 'gold', fontSize: 24 }} />
                  </Box>
                  
                  {/* Notification Content */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      🎉 Big Earner Alert!
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                      {notificationMessage}
                    </Typography>
                  </Box>
                  
                  {/* Close Button - Using Close icon */}
                  <IconButton 
                    size="small" 
                    onClick={() => setShowNotification(false)}
                    sx={{ 
                      color: 'white',
                      '&:hover': { 
                        backgroundColor: 'rgba(255,255,255,0.1)' 
                      }
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
                
                {/* Progress bar for auto-dismiss */}
                <LinearProgress 
                  variant="determinate" 
                  value={0}
                  sx={{ 
                    mt: 1,
                    height: 3,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'gold',
                      animation: 'progress 4s linear'
                    },
                    '@keyframes progress': {
                      '0%': { transform: 'scaleX(0)' },
                      '100%': { transform: 'scaleX(1)' }
                    }
                  }}
                />
              </Paper>
            )}
    </Box>
  );
}